<template>
  <div
    v-if="popupState"
    id="popup-component"
    ref="modal"
    class="modal-mask fixed z-[9998] top-0 left-0 w-full h-full table"
    :class="!sidebarVariant && 'bg-[rgba(0,0,0,0.5)]'"
  >
    <div
      :class="!sidebarVariant ? 'modal-wrapper table-cell align-middle' : 'h-full'"
      @mousedown.prevent="onOutsideInteraction"
      @touchstart.prevent="onOutsideInteraction"
    >
      <div class="inline-block w-full" @touchstart.stop>
        <div
          role="dialog"
          aria-modal="true"
          :aria-label="popupHeader"
          :class="`modal-content bg-foreground box-shadow-static-lg px-16 pb-16 w-full rounded-t-xl bottom-0 fixed sm:static sm:m-auto sm:rounded-xl overflow-auto ${sidebarClass} ${widthClass} ${customClasses}`"
          @mousedown.stop
          @touchstart.stop
        >
          <div class="bg-foreground sticky top-0 z-[1]">
            <div class="flex py-16">
              <h2 :class="`${headerClass} text-grey-header`">{{ popupHeader }}</h2>
              <button v-if="!hideCloseButton" class="primary-focus ml-auto" aria-label="Close" @click="closePopup">
                <CloseIcon color="#4B4C52" aria-hidden="true" />
              </button>
            </div>
            <hr v-if="hasHeaderDivider" class="border-grey-divider-subdued -mx-16" />
          </div>
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import CloseIcon from '../../graphics/icons/CloseIcon.vue';

export default {
  name: 'Popup',
  components: {
    CloseIcon,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: {
      type: Boolean,
      default: undefined,
    },
    value: {
      type: Boolean,
      default: false,
    },
    popupHeader: {
      type: String,
      required: true,
    },
    popupSize: {
      type: String,
      default: 'sm',
    },
    headerClass: {
      type: String,
      default: 'text-h4',
    },
    customClasses: {
      type: String,
      default: '',
    },
    hasHeaderDivider: {
      type: Boolean,
      default: false,
    },
    sidebarVariant: {
      type: Boolean,
      default: false,
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: true,
    },
    hideCloseButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'input', 'update:modelValue'],
  data() {
    return {
      popupState: false,
      popupInitialized: false,
      tabKeydownListener: null as ((_e: KeyboardEvent) => void) | null,
      escapeKeydownListener: null as ((_e: KeyboardEvent) => void) | null,
    };
  },
  computed: {
    parentOpen(): boolean {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    widthClass(): string {
      return this.popupSize === 'lg' ? 'sm:max-w-664' : 'sm:max-w-368';
    },
    sidebarClass(): string {
      return this.sidebarVariant ? 'sidebar-variant overflow-y-hidden' : 'max-h-[85vh]';
    },
  },
  watch: {
    parentOpen: {
      immediate: true,
      handler(parentVal: boolean) {
        this.popupState = parentVal;
      },
    },
    popupState(value: boolean) {
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
      if (value) {
        this.openPopup();
      } else {
        this.closePopup();
      }
    },
  },
  beforeUpdate() {
    if (this.popupState) {
      // save the element that triggered the popup so that we can return the user's focus to it when the popup is closed
      document.activeElement?.classList.add('modal-trigger');
    }
  },
  beforeUnmount() {
    this.removeKeydownListeners();
    document.removeEventListener('keydown', this.closePopup);
  },
  updated() {
    if (this.popupState && !this.popupInitialized) {
      const modal = this.$refs.modal as HTMLElement;
      const focusableEls = modal?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      const firstFocusable = focusableEls[0] as HTMLElement;
      const lastFocusable = focusableEls[focusableEls.length - 1] as HTMLElement;
      this.addModalTabLoop(firstFocusable, lastFocusable);
      this.popupInitialized = true;
    }
  },
  methods: {
    openPopup() {
      this.popupState = true;
    },
    onOutsideInteraction() {
      if (!this.closeOnOutsideClick) return;
      this.closePopup();
    },
    closePopup() {
      this.popupState = false;
      this.popupInitialized = false;
      this.$emit('close');
      const triggerElement = document.querySelector('.modal-trigger') as HTMLElement;

      // return the focus to the element that triggered the popup
      // this is so that if the user is navigating with the keyboard, they can continue to do so after closing the popup
      if (triggerElement) {
        triggerElement.focus();
        triggerElement.classList.remove('modal-trigger');
      }

      this.removeKeydownListeners();
    },
    addModalTabLoop(firstFocus: HTMLElement, lastFocus: HTMLElement) {
      firstFocus?.focus();

      this.tabKeydownListener = ((e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === firstFocus) {
            lastFocus.focus();
            e.preventDefault();
          }
        } else if (document.activeElement === lastFocus) {
          firstFocus.focus();
          e.preventDefault();
        }
      }) as (_e: KeyboardEvent) => void;

      this.escapeKeydownListener = ((e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        // Escape mirrors the close button — skip when that control is hidden.
        if (this.hideCloseButton) return;
        this.closePopup();
      }) as (_e: KeyboardEvent) => void;

      document.addEventListener('keydown', this.tabKeydownListener);
      document.addEventListener('keydown', this.escapeKeydownListener);
    },
    removeKeydownListeners() {
      if (this.tabKeydownListener) {
        document.removeEventListener('keydown', this.tabKeydownListener);
        this.tabKeydownListener = null;
      }
      if (this.escapeKeydownListener) {
        document.removeEventListener('keydown', this.escapeKeydownListener);
        this.escapeKeydownListener = null;
      }
    },
  },
};
</script>

<style>
/* hide cookie banner when popup is open */
main:has(#popup-component) ~ #onetrust-consent-sdk #ot-sdk-btn-floating {
  z-index: 1 !important;
}

.sidebar-variant {
  height: 100%;
  margin-right: 0;
  @media only screen and (min-width: 448px) {
    height: 100vh;
  }
}
</style>
